import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { Mail, Phone, Globe2, GraduationCap, Briefcase, ArrowRight, ArrowLeft, Users } from 'lucide-react';
import { useLocalizedData } from '../i18n/dataLoader';

const Team = () => {
  const { t } = useTranslation();
  const data = useLocalizedData('team');
  const teamMembers = data?.teamMembers || [];
  // Build ItemList of Person schemas for AI/search engines
  const teamJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: teamMembers.map((m, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Person',
        name: `${m.name}${m.suffix ? ' ' + m.suffix : ''}`,
        jobTitle: m.title || m.role,
        image: m.image ? `https://www.kanzlei-partner.com${m.image}` : undefined,
        worksFor: { '@id': 'https://www.kanzlei-partner.com/#kanzlei' },
      },
    })),
  };
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Unser Team – Kanzlei Nabhan Gäbler & Partner"
        description="Lernen Sie unser Team aus erfahrenen Rechtsanwälten, Fachanwälten und Steuerberatern kennen. Persönliche Beratung auf Augenhöhe."
        path="/team"
        jsonLd={teamJsonLd}
      />
      <Header />
      <main className="pt-[150px]">
        {/* Hero */}
        <section className="bg-[#1B3A5C] text-white py-16 lg:py-24 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <Link to="/" className="flex w-fit items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back_home', 'Zurück zur Startseite')}
            </Link>
            <div className="inline-flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              {t('team.eyebrow', 'UNSER TEAM')}
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5">
              {t('team.headline', 'Menschen, die für Sie kämpfen')}
            </h1>
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl">
              {t('team.intro', 'Ein eingespieltes Team aus erfahrenen Anwälten, steuerlichen Beratern und Experten — alle vereint von einem Anspruch: Für Sie das bestmögliche Ergebnis zu erreichen.')}
            </p>
          </div>
        </section>

        {/* Partners */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('team.partners_eyebrow', 'UNSERE PARTNER')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{t('team.partners_headline', 'Spezialisten — ein Team')}</h2>
            </div>

            <div className="space-y-12">
              {teamMembers.map((m, idx) => (
                <div
                  key={m.id}
                  className={`grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`lg:col-span-2 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative">
                      <img
                        src={m.image}
                        alt={m.name}
                        className="w-full aspect-[3/4] object-cover rounded-sm shadow-xl"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1B3A5C]/80 to-transparent p-6 rounded-sm">
                        <p className="text-white font-serif text-xl">
                          {m.name}
                          {m.suffix && <span className="ml-2">{m.suffix}</span>}
                        </p>
                      </div>
                      <div className="absolute -top-3 -right-3 px-4 py-1.5 bg-[#b8893d] text-white text-[10px] tracking-[0.2em] shadow-lg">
                        {(m.title || '').toUpperCase()}
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-3 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="mb-8">
                      {m.languages && m.languages.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 text-[#b8893d] text-xs tracking-[0.2em] mb-2">
                            <Globe2 className="w-3.5 h-3.5" /> {t('team.languages', 'SPRACHEN')}
                          </div>
                          <ul className="space-y-1">
                            {m.languages.map((l) => (
                              <li key={l} className="text-[#1B3A5C] text-sm font-medium">{l}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <a href={`tel:${m.phone}`} className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#b8893d] text-sm font-medium">
                        <Phone className="w-4 h-4 text-[#b8893d]" />
                        {m.phone}
                      </a>
                      <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#b8893d] text-sm font-medium">
                        <Mail className="w-4 h-4 text-[#b8893d]" />
                        {m.email}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Support staff */}
        <section className="py-16 bg-[#f5f8fa]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('team.office_eyebrow', 'UNSER BÜRO-TEAM')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{t('team.office_headline', '9 Mitarbeiter im Hintergrund')}</h2>
            <p className="text-[#34536b] mt-5 text-base leading-relaxed">
              {t('team.office_body', 'Hinter den Kulissen unserer Kanzlei arbeiten 9 weitere Mitarbeiter – Rechtsanwaltsfachangestellte, Steuerfachangestellte, Akademiker und unsere Verwaltung. Sie sind die organisatorische Schaltzentrale, kümmern sich um Termine, Schriftsätze, Buchhaltung und Lohnabrechnung – damit wir uns voll auf Ihr Anliegen konzentrieren können.')}
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 bg-white border border-[#b8893d]/30 rounded-sm">
              <Users className="w-5 h-5 text-[#b8893d]" />
              <span className="text-[#1B3A5C] text-sm tracking-wide">{t('team.office_badge', '14 Köpfe · ein Team · ein Anspruch')}</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#1B3A5C] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="font-serif text-3xl md:text-4xl mb-5">{t('team.cta_headline', 'Lernen Sie uns persönlich kennen')}</h2>
            <p className="text-white/80 leading-relaxed mb-10 max-w-2xl mx-auto">
              {t('team.cta_body', 'Buchen Sie eine Erstberatung (200 € pauschal, anrechenbar) bei dem Partner, der zu Ihrem Anliegen passt.')}
            </p>
            <Link to="/mandantenerfassung">
              <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white px-6 sm:px-10 py-6 tracking-[0.1em] sm:tracking-[0.15em] text-xs whitespace-normal h-auto max-w-full">
                {t('team.cta_button', 'JETZT ERSTBERATUNGSTERMIN ANFRAGEN')} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default Team;
